/**
 * StudyCase Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore (NOT firebase-admin).
 * Converts between StudyCase TypeScript type and Firestore data.
 *
 * Based on strategyConverter pattern from firebase-core module.
 *
 * CRITICAL: Uses Client SDK signature fromFirestore(snapshot, options)
 * NOT Admin SDK signature fromFirestore(data)
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp } from 'firebase/firestore';
import type { StudyCase } from '@segmently/schema-models/entities';

export const studyCaseConverter: FirestoreDataConverter<StudyCase> = {
  /**
   * Convert StudyCase model to Firestore data
   * Called when writing to Firestore (create, update operations)
   *
   * Date Field Mapping:
   * - createdAt (string/Date) → createdAt (Timestamp)
   */
  toFirestore(studyCase: StudyCase): any {
    const result: any = { ...studyCase };

    // Convert Date/string to Firestore Timestamp for createdAt
    if (typeof result['createdAt'] === 'string') {
      result['createdAt'] = Timestamp.fromDate(new Date(result['createdAt']));
    } else if (result['createdAt'] instanceof Date) {
      result['createdAt'] = Timestamp.fromDate(result['createdAt']);
    }

    return result;
  },

  /**
   * Convert Firestore data to StudyCase model
   * Called when reading from Firestore (get, query, snapshot operations)
   *
   * CRITICAL: Uses Client SDK signature (snapshot, options) NOT Admin SDK signature (data)
   *
   * Date Field Mapping:
   * - createdAt (Timestamp) → createdAt (ISO string)
   */
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): StudyCase {
    // Extract data from snapshot using Client SDK method
    const data = snapshot.data(options);
    const result: any = { ...data };

    // Add document ID to result
    result.id = snapshot.id;

    // Convert Firestore Timestamp to ISO string for createdAt
    if (result['createdAt']?.toDate) {
      result['createdAt'] = result["createdAt"].toDate();
    }

    return result as StudyCase;
  }
};

export default studyCaseConverter;
